import MaintenanceEditor from "@/components/maintenace/MaintenanceEditor.vue";
import Loader from "@/components/loader/Loader.vue";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import {MaintenanceFormStatus} from "@/enums/MaintenanceFormStatus";
import { MaintenanceMixin } from '@/mixins/Maintenance';
import firebase from 'firebase/app';
import { serializer } from '@/api/shared/serializer';

export default {
  name: "MaintenanceTemplatesView",
  mixins: [MaintenanceMixin],
  components: {
    MaintenanceEditor,
    Loader,
    ConfirmDlg
  },
  data() {
    return {
      loading: true,
      saving: false,
      isDisabledSave: true,
      isSurveyModified: false,
      isNameEditMode: false,
      editorKey: 0,
      originalName: null,
      originalTemplateStr: null,
      enableEditManually: false
    }
  },
  async mounted() {
    await this.$store.dispatch("getMaintenanceTemplate", this.$route.params.id);
    await this.$store.dispatch('getScheduledMaintenance');
    await this.$store.dispatch('getScheduledMaintenanceRequests');
    this.originalName = this.template.name || ''
    this.originalTemplateStr = this.template.template || ''
    this.loading = false;
  },
  computed: {
    template() {
      return this.$store.state.maintenanceModule.maintenanceTemplate;
    },
    scheduledMaintenances() {
      return this.$store.state.maintenanceModule.scheduledMaintenance;
    },
    scheduledMaintenanceRequests() {
      return this.$store.state.maintenanceModule.scheduledMaintenanceRequests;
    },
    isTemplateAlreadyUsedInScheduled() {
      return !!this.scheduledMaintenances.filter(req => req.forms?.some(form => form.templateId === this.$route.params.id)).length;
    },
    templateAlreadyUsedInScheduled() {
      return this.scheduledMaintenances
        .filter(req => req.forms?.some(form => form.templateId === this.$route.params.id))
        .map(req => req.title)
        .join(', ');
    },
    isTemplateAlreadyCompletedInScheduled() {
      return !!this.scheduledMaintenanceRequests
        .filter(req => req.forms?.some(form => form.templateId === this.$route.params.id))
        .filter(req => req.forms?.some(form => form.status === MaintenanceFormStatus.Completed)).length;
    },
    templateAlreadyCompletedInScheduled() {
      return this.scheduledMaintenanceRequests
        .filter(req => req.forms?.some(form => form.templateId === this.$route.params.id))
        .filter(req => req.forms?.some(form => form.status === MaintenanceFormStatus.Completed))
        .map(req => req.name)
        .join(', ');
    },
    computedDisabled() {
      return this.isTemplateAlreadyCompletedInScheduled && !this.enableEditManually;
    }
  },
  methods: {
    backToList() {
      this.$router.push('/maintenanceTemplates');
    },
    modify() {
      this.isDisabledSave = true;
    },
    async deleteTemplate() {
      const response = await this.$refs.confirmDlg.open(null, this.$t('MaintenanceTemplates.MaintenanceTemplateDeletedWarning'), false);
      if (!response || !response.agree) return;

      this.saving = true;

      try {
        await this.$store.dispatch("deleteMaintenanceTemplate", this.$route.params.id);

        this.$toast.open({
          message: this.$t("MaintenanceTemplates.MaintenanceTemplateDeleted"),
          type: "success"
        });

        this.$router.push("/maintenanceTemplates");
      } catch (err) {
        this.$toast.open({
          message: err.message,
          type: "error"
        });
      } finally {
        this.saving = false;
      }
    },
    async saveTemplate() {
      this.saving = true;

      try {
        const newName = (this.template.name || '').trim()
        const prevName = (this.originalName || '').trim()
        let updatedTemplate = this.template.template
        let nameChanged = false

        if (newName && newName !== prevName) {
          this.isNameEditMode = false
          this.template.name = newName
          const json = JSON.parse(this.template.template)
          json.title = newName
          updatedTemplate = JSON.stringify(json)
          nameChanged = true
        }

        // Compare current JSON string vs cached original
        const currentTemplateStr = typeof updatedTemplate === 'string' ? updatedTemplate : JSON.stringify(updatedTemplate)
        const isTemplateChanged = currentTemplateStr !== (this.originalTemplateStr || '')

        // Early exit if nothing changed at all
        if (!nameChanged && !isTemplateChanged) {
          this.isNameEditMode = false
          this.saving = false
          return
        }

        await this.$store.dispatch("saveMaintenanceTemplate", {
          id: this.$route.params.id,
          template: {
            name: this.template.name,
            template: currentTemplateStr
          }
        });

        if(this.enableEditManually) {
          await this.updateMaintenanceForms();
        }

        if (nameChanged) this.editorKey++ // re-render only if name changed
        this.originalName = this.template.name // update cached name
        this.originalTemplateStr = currentTemplateStr // update cached JSON

        this.$toast.open({
          message: this.$t("MaintenanceTemplates.MaintenanceTemplateUpdated"),
          type: "success"
        });

      } catch (error) {
        this.$toast.open({ message: 'Error updating template', type: "error" });
        console.error('Error saving template:', error);
      } finally {
        this.saving = false;
        this.isDisabledSave = true;
        this.isNameEditMode = false;
        this.enableEditManually = false;
      }
    },
    async editTemplate() {
      const response = await this.$refs.confirmDlg.open(null, this.$t('MaintenanceTemplates.MaintenanceTemplateUpdateWarning'), false, '', { noButtonText: this.$t('GeneralTranslations.Cancel'), yesButtonText: this.$t('GeneralTranslations.Continue') });
      if (!response || !response.agree) return;

      this.enableEditManually = true;
      this.$forceUpdate();
    },
    async updateMaintenanceForms() {
      try {
        const querySnapshot = await firebase.firestore()
          .collection('maintenanceForms')
          .where('templateId', '==', this.$route.params.id)
          .where('status', '==', MaintenanceFormStatus.InProgress)
          .get();

        const data = serializer.querySnapshotToDocuments(querySnapshot);
        const elements = JSON.parse(this.template.template).pages[0].elements;
        const allowedKeys = new Set(elements.map(item => item.name));

        const updates = data.map((item: any) => {
          const filteredResult = Object.fromEntries(Object.entries(item.result).filter(([key]) => allowedKeys.has(key)));

          return firebase.firestore()
            .collection('maintenanceForms')
            .doc(item.id)
            .update({
              template: this.template.template,
              result: filteredResult
            });
        });

        await Promise.all(updates);
      } catch (error) {
        console.error("UPDATE MAINTENANCE FORMS FAILED:", error);
      }
    }
  },
  watch: {
    'template.template'(newValue, oldValue) {
      if (newValue !== oldValue) {
        setTimeout(() => {
          this.isDisabledSave = false
        }, 3000)
      }
    }
  }
}